import { Component, OnInit, AfterViewInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../models/user';
import { AuthService } from '../services/authService';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { validate } from 'class-validator';
import { catchError, Observable, of } from 'rxjs';
import { EditRoleViewModel } from '../models/editRoleViewModel';
import { NgModel } from '@angular/forms';
import { UserRoleViewModel } from '../models/userRoleViewModel';

@Component({
    selector: 'app-edit-role',
    templateUrl: './edit-role.component.html',
    styleUrl: './edit-role.component.scss',
    standalone: false
})
export class EditRoleComponent {
  user = new User('', false);
  http!: HttpClient;
  model = new EditRoleViewModel("", "", "", []);
  userRoleViewModels: UserRoleViewModel[] = [];
  errorList = document.createElement('ul');
  response?: any;
  errorTitle?: string;
  roleNameErrorMsg?: string;
  descriptionErrorMsg?: string;
  roleIdErrorMsg?: string;
  @ViewChild('roleName') roleName!: NgModel;
  @ViewChild('roleDescription') roleDescription!: NgModel;
  @ViewChild('roleId') roleId!: NgModel;

  constructor(http: HttpClient, private authService: AuthService, private route: ActivatedRoute, private renderer: Renderer2, private router: Router) {
    this.http = http;
  }

  public async ngOnInit() {
    // Extract the query parameter;
    this.route.queryParams.subscribe(params => {
      this.model.id = params['roleId'];
      this.model.roleName = params['roleName'];
      this.model.description = params['description'];
    });
    const isLoggedIn = await this.checkIfUserIsLoggedIn();
    if (isLoggedIn) {
      this.getUsersInRole(this.model.id);
    }
  }

  private ngAfterViewInit() {
    // Set the values for the ViewChild elements
    if (this.roleId && this.roleName) {
      this.roleId.control.setValue(this.model.id);
      this.roleName.control.setValue(this.model.roleName);
      this.roleDescription.control.setValue(this.model.description);
    }
  }

  private async getUsersInRole(roleId: string) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    this.http
      .get<any>('/api/Administration/get_users_in_specified_role', {
        headers: headers,
        params: { roleId: roleId }
      })
      .pipe(catchError(this.handleError<any>('getUsersInRole')))
      .subscribe({
        next: (result) => {
          if (result.success === true) {
            this.roleId = result.roleId;
            this.roleName = result.roleName;
            this.roleDescription = result.roleDescription;
            this.userRoleViewModels = result.userRoleViewModels;
            this.updateModelWithListOfUsersInThisRole(this.userRoleViewModels);
          }
          else {
            if (result.errorMessages) {
              for (const errorMessage of result.errorMessages) {
                console.error(errorMessage);
              }
            }
          }
        },
        error: (error) => console.error(error),
      });
  }

  private updateModelWithListOfUsersInThisRole(userRoleViewModels: UserRoleViewModel[]) {
    for (const userRoleViewModel of userRoleViewModels) {
      this.model.users.push(userRoleViewModel);
    }
  }

  private checkIfUserIsLoggedIn(): Promise<boolean> {
    return new Promise((resolve) => {
      this.authService.isLoggedIn().subscribe((response: any) => {
        this.user.loggedin = response.isAuthenticated;
        this.user.username = response.userName ?? "";
        resolve(this.user.loggedin);
      });
    });
  }

  public async editRole() {
    try {
      const errors = await validate(this.model);
      if (errors.length > 0) {
        console.error('Validation failed. Errors: ', errors);
        errors.forEach(error => {
          if (error.property === 'roleName') {
            this.roleNameErrorMsg = error.constraints ? error.constraints['IsNotEmpty'] || 'Role name is required' : 'Role name is required';
          }
          if (error.property === 'id') {
            this.roleIdErrorMsg = error.constraints ? error.constraints['IsNotEmpty'] || 'Id is required' : 'Id is required';
          }
        });
      } else {
        this.roleNameErrorMsg = "";
        this.roleIdErrorMsg = "";
      }
    } catch (error) {
      console.error('Validation error: ', error);
    }

    if (this.roleNameErrorMsg || this.roleIdErrorMsg) {
      return;
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': '*/*'
    });
    this.http.put('/api/Administration/editrole', this.model, {
      headers: headers,
    })
      .pipe(catchError(this.handleError<any>('editRole')))
      .subscribe({
        next: (response) => {
          this.response = response;
          if (!response) {
            console.error("response is undefined");
          }
          else if (response?.success == false) {
            for (let errorMessage = 0; errorMessage < response.errorMessages.length; errorMessage++) {
              if (response.errorMessages[errorMessage].indexOf("RoleName") != -1) {
                this.roleName.control.setErrors({ invalid: true });
                this.roleNameErrorMsg = response.errorMessages[errorMessage];
              }
              else if (response.errorMessages[errorMessage].indexOf("Id") != -1) {
                this.roleId.control.setErrors({ invalid: true });
                this.roleIdErrorMsg = response.errorMessages[errorMessage];
              }

              const li = this.renderer.createElement('li');
              const text = this.renderer.createText(response.errorMessages[errorMessage]);
              this.renderer.appendChild(li, text);
              this.renderer.appendChild(this.errorList, li);
            }
          } else {
            this.router.navigate(['/list_roles']);
          }
        },
        error: (error) => {
          this.response = error;
          console.error(error.message);
        },
      });
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      if (error.error) {
        this.errorTitle = error.error.title;
      }
      for (let key in error.error.errors) {
        if (key.toLowerCase() == "roleName") {
          this.roleNameErrorMsg = error.error.errors[key];
        }
      }

      return of(result as T);
    };
  }
}
